import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Users, Globe, Sparkles, MessageCircle, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_NUMBER = "+919068155122";
const INSTAGRAM = "https://www.instagram.com/karigarihubindia";

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "KarigariHub | Custom Lehenga & Hand Embroidery India",
    description: "India's leading custom lehenga and embroidery platform. Bridal lehenga, Zardozi, Aari, Mirror Work by master karigars. WhatsApp +91 9068155122.",
    keywords: "hand embroidery services India, custom lehenga embroidery, bridal lehenga, lehenga, lehengas, lehenga for women, lehenga designs, designer lehenga, hand embroidery services near me, custom hand embroidery, Zardozi embroidery, Aari work, bridal embroidery India, lehenga choli, mirror work lehenga, gota patti lehenga, embroidered lehenga, KarigariHub, embroidery artisans India, designer embroidery, bulk embroidery orders, wedding lehenga, dulhan lehenga",
    aiDescription: "KarigariHub is India's premier hand embroidery and custom lehenga platform offering 17 traditional techniques: Zardozi, Aari, Dabka, Mirror Work, Tilla, Mukaish, Pearl, Crystal, Cutdana, Beadwork, Sequin, Gota Patti, Jaal, Jadau, Leather, Applique, Resham. Custom lehenga for women, bridal lehenga designs, designer lehenga choli, wedding lehenga embroidery. Orders for bridal, couture, fashion designers, boutiques. Lehenga online ordering with worldwide delivery. B2B bulk orders available. Contact: +91 9068155122, karigarihubindia@gmail.com.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "KarigariHub",
      "description": "India's premier custom lehenga and hand embroidery platform. Bridal lehenga, designer lehenga, and custom embroidery services.",
      "url": "https://karigarihubindia.com",
      "telephone": "+919068155122",
      "address": { "@type": "PostalAddress", "addressCountry": "IN" },
      "sameAs": ["https://www.instagram.com/karigarihubindia"]
    },
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.slice(0, 6));
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = (message = "Hello! I'm interested in your hand embroidery services.") => {
    window.open(`https://wa.me/${PHONE_NUMBER.replace('+', '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleGetQuote = (serviceName) => {
    handleWhatsApp(`Hello! I want to get a quote for ${serviceName} hand embroidery work.`);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-section pt-20 flex items-center" data-testid="hero-section">
        <div className="container-main w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[calc(100vh-80px)] py-12">
            <div className="space-y-8 animate-fade-in-up">
              <div className="accent-line" />
              <div>
                <p className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">Professional Hand Embroidery Services</p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
                  Professional<br />
                  <span className="text-[#D4AF37]">Hand Embroidery</span><br />
                  Services for Designers,<br />
                  Brands & Individuals.
                </h1>
              </div>
              <p className="font-body text-base lg:text-lg text-muted-foreground max-w-lg leading-relaxed">
                Connect with India's finest artisans for bespoke hand embroidery. From royal Zardozi to 
                delicate Mukaish — 17 traditional techniques, one destination. Every stitch tells a story.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary flex items-center gap-2 bg-[#25D366] hover:bg-[#25D366]/90"
                  onClick={() => handleWhatsApp()}
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  Get Started on WhatsApp
                </Button>
                <Link to="/services" data-testid="hero-explore-btn">
                  <Button variant="outline" className="btn-secondary flex items-center gap-2 w-full sm:w-auto">
                    Explore Services
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-3 gap-8 pt-8 border-t border-border">
                <div>
                  <p className="font-heading text-2xl lg:text-3xl font-semibold">17</p>
                  <p className="text-sm text-muted-foreground">Embroidery Types</p>
                </div>
                <div>
                  <p className="font-heading text-2xl lg:text-3xl font-semibold">100%</p>
                  <p className="text-sm text-muted-foreground">Handcrafted</p>
                </div>
                <div>
                  <p className="font-heading text-2xl lg:text-3xl font-semibold">Pan-India</p>
                  <p className="text-sm text-muted-foreground">Artisan Network</p>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-up delay-200">
              <div className="aspect-[4/5] overflow-hidden bg-secondary">
                <img 
                  src="/images/hero-embroidery.jpg"
                  alt="Professional hand embroidery Zardozi work on silk fabric by Indian artisan - KarigariHub"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "https://images.pexels.com/photos/2381469/pexels-photo-2381469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"; }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-float max-w-xs hidden lg:block">
                <p className="font-accent italic text-lg text-muted-foreground">
                  "Every thread tells a story, every stitch carries tradition."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-spacing bg-white" data-testid="services-section">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="accent-line mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4">
              Hand Embroidery Services
            </h2>
            <p className="text-muted-foreground">
              17 traditional Indian embroidery techniques, each mastered by skilled artisans. 
              Choose your style and let us bring your vision to life.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-secondary animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <div key={service.id} className="service-card group" data-testid={`service-card-${service.id}`}>
                  <div className="service-card-image relative">
                    <img 
                      src={service.images?.[0]} 
                      alt={`${service.name} hand embroidery service India - KarigariHub`} 
                      loading="lazy"
                      onError={(e) => { e.target.src = "https://images.pexels.com/photos/2381469/pexels-photo-2381469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"; }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors duration-500 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 gap-3">
                      <Link to="/services">
                        <Button className="bg-white text-[#1A1A1A] hover:bg-white/90">View Details</Button>
                      </Link>
                      <Button 
                        className="bg-[#25D366] text-white hover:bg-[#25D366]/90 flex items-center gap-2"
                        onClick={() => handleGetQuote(service.name)}
                        data-testid={`get-quote-${service.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        Get Quote
                      </Button>
                    </div>
                  </div>
                  <div className="service-card-content">
                    <h3 className="font-heading text-xl font-semibold">{service.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{service.origin}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services-btn">
              <Button className="btn-secondary">
                View All 17 Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner - Get Any Embroidery Done */}
      <section className="py-16 bg-[#1A1A1A]" data-testid="cta-banner">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Need Any Type of Hand Embroidery?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Whatever your embroidery requirement — bridal, fashion, home decor, or custom designs — 
            our skilled artisans can bring your vision to life. Send us a message right now!
          </p>
          <Button 
            className="bg-white text-[#1A1A1A] hover:bg-white/90 px-8 py-3 text-lg"
            onClick={() => handleWhatsApp("Hello! I need custom hand embroidery work done. Can we discuss my requirements?")}
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Message Us on WhatsApp
          </Button>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing" data-testid="how-it-works-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="accent-line mb-6" />
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
                How It Works
              </h2>
              <p className="text-muted-foreground mb-12 max-w-md">
                A seamless journey from your idea to finished embroidery. Transparent process, fair pricing.
              </p>

              <div className="relative space-y-12 pl-12">
                <div className="timeline-line" />
                <div className="relative">
                  <div className="timeline-dot absolute -left-12">1</div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Share Your Idea</h3>
                  <p className="text-muted-foreground text-sm">Send your design, reference images, or idea on WhatsApp. We'll discuss fabric, technique, and details.</p>
                </div>
                <div className="relative">
                  <div className="timeline-dot absolute -left-12">2</div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Get Quote & Timeline</h3>
                  <p className="text-muted-foreground text-sm">Receive a detailed quote and timeline. Pay 65% advance to start work.</p>
                </div>
                <div className="relative">
                  <div className="timeline-dot absolute -left-12">3</div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Video Approval & Delivery</h3>
                  <p className="text-muted-foreground text-sm">We show finished work on video call. Pay remaining 35% only after you're satisfied, then we ship.</p>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/faq"><Button variant="link" className="text-[#D4AF37] p-0">Read Full FAQ →</Button></Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden">
                <img 
                  src="/images/artisan-work.jpg" 
                  alt="Indian hand embroidery artisan karigar at work - traditional craftsmanship" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1701188516156-10b389bb8e94?crop=entropy&cs=srgb&fm=jpg"; }}
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden mt-12">
                <img 
                  src="/images/embroidery-detail.jpg" 
                  alt="Close-up of hand embroidery detail showing intricate Indian needlework" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "https://images.pexels.com/photos/33101418/pexels-photo-33101418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"; }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join as Artisan */}
      <section className="py-20 bg-[#1A1A1A] text-white" data-testid="join-cta-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-12 h-px bg-[#D4AF37] mb-6" />
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
                Are You an Embroidery Artisan?
              </h2>
              <p className="text-white/60 mb-8 max-w-md">
                Join our network of skilled craftspeople. Work on projects, hourly, or monthly basis. 
                Fair pay, regular work, direct client connections.
              </p>
              <Link to="/register" data-testid="join-register-btn">
                <Button className="bg-white text-[#1A1A1A] hover:bg-white/90 px-8 py-3 text-sm uppercase tracking-widest">
                  Register Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-6 border border-white/10">
                <Users className="w-8 h-8 mx-auto mb-4 text-[#D4AF37]" />
                <p className="font-heading text-lg font-semibold">Project Based</p>
              </div>
              <div className="text-center p-6 border border-white/10">
                <Sparkles className="w-8 h-8 mx-auto mb-4 text-[#D4AF37]" />
                <p className="font-heading text-lg font-semibold">Hourly</p>
              </div>
              <div className="text-center p-6 border border-white/10">
                <Globe className="w-8 h-8 mx-auto mb-4 text-[#D4AF37]" />
                <p className="font-heading text-lg font-semibold">Monthly</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Tools — Engagement Section */}
      <section className="section-spacing bg-white" data-testid="quick-tools">
        <div className="container-main">
          <div className="text-center mb-10">
            <div className="accent-line mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-3">Explore & Get Started</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Find your perfect lehenga design, estimate costs, or get a custom quote — all in minutes.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            <Link to="/lehenga-inspiration" className="group p-6 border border-border rounded-xl hover:border-[#D4AF37]/40 hover:shadow-md transition-all" data-testid="tool-inspiration">
              <Sparkles className="w-6 h-6 text-[#D4AF37] mb-3" />
              <h3 className="font-heading font-semibold mb-1">Lehenga Inspiration</h3>
              <p className="text-xs text-muted-foreground mb-3">Browse designs by color, occasion, and technique</p>
              <span className="text-xs text-[#D4AF37] group-hover:underline inline-flex items-center gap-1">Explore <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/estimate" className="group p-6 border border-border rounded-xl hover:border-[#D4AF37]/40 hover:shadow-md transition-all" data-testid="tool-estimator">
              <Sparkles className="w-6 h-6 text-[#D4AF37] mb-3" />
              <h3 className="font-heading font-semibold mb-1">Cost Estimator</h3>
              <p className="text-xs text-muted-foreground mb-3">Get instant indicative pricing for your project</p>
              <span className="text-xs text-[#D4AF37] group-hover:underline inline-flex items-center gap-1">Calculate <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/get-your-lehenga" className="group p-6 border border-border rounded-xl hover:border-[#D4AF37]/40 hover:shadow-md transition-all" data-testid="tool-custom">
              <Sparkles className="w-6 h-6 text-[#D4AF37] mb-3" />
              <h3 className="font-heading font-semibold mb-1">Get Your Lehenga Made</h3>
              <p className="text-xs text-muted-foreground mb-3">Upload a reference and get a custom quote</p>
              <span className="text-xs text-[#D4AF37] group-hover:underline inline-flex items-center gap-1">Upload <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/custom-bridal-lehengas" className="group p-6 border border-border rounded-xl hover:border-[#D4AF37]/40 hover:shadow-md transition-all" data-testid="tool-bridal">
              <Sparkles className="w-6 h-6 text-[#D4AF37] mb-3" />
              <h3 className="font-heading font-semibold mb-1">Bridal Embroidery</h3>
              <p className="text-xs text-muted-foreground mb-3">Custom bridal lehenga, dupatta, and blouse work</p>
              <span className="text-xs text-[#D4AF37] group-hover:underline inline-flex items-center gap-1">View <ArrowRight className="w-3 h-3" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* B2B CTA */}
      <section className="section-spacing bg-[#F5F0E8]" data-testid="b2b-section">
        <div className="container-main text-center">
          <div className="accent-line mx-auto mb-6" />
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
            Bulk Orders & B2B
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Fashion houses, boutiques, designers, and manufacturers — we offer dedicated B2B services 
            with bulk pricing, priority support, and reliable delivery.
          </p>
          <Link to="/b2b" data-testid="b2b-cta-btn">
            <Button className="btn-primary">
              B2B Enquiry
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Follow Us */}
      <section className="py-16 bg-white" data-testid="social-section">
        <div className="container-main text-center">
          <h2 className="font-heading text-2xl font-semibold mb-4">Follow Our Work</h2>
          <p className="text-muted-foreground mb-6">See our latest projects and behind-the-scenes on Instagram</p>
          <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="flex items-center gap-2 mx-auto">
              <Instagram className="w-5 h-5" />
              @karigarihubindia
            </Button>
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing" data-testid="contact-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="accent-line mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-muted-foreground mb-8">
              Whether you need a single piece or bulk orders, we're here to help. Reach out today!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                className="btn-primary flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#25D366]/90"
                onClick={() => handleWhatsApp()}
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp: {PHONE_NUMBER}
              </Button>
              <a href={`tel:${PHONE_NUMBER}`} data-testid="contact-call-btn">
                <Button variant="outline" className="btn-secondary w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
